/**
 * Diwali drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const diwaliDrawingsPack: ToilePack = {
    slug: 'diwali-drawings',
    category: 'coloring',
    name: 'Diwali drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'diyas', label: 'Diyas and rangoli', subject: 'three lit diyas on a rangoli with marigold petals'},
        {key: 'house', label: 'Lit house', subject: 'a house with lamps on every window and a lantern hanging at the door'},
        {key: 'children', label: 'Sparklers', subject: 'two children holding sparklers in front of a house with fireworks above'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a lantern', 'sweets', 'a night sky'],
    directionHint: 'try: a lantern',
    seo: {
        title: 'Diwali Drawings: Diyas and Rangoli, Lit House, Sparklers',
        metaDescription: 'Nine Diwali drawings: diyas on a rangoli, a lit house and children with sparklers, in pencil sketch, colour pencil and outline.',
        h1: 'Diwali drawings: diyas and rangoli, lit house, children with sparklers',
        tagline: 'Three Diwali scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Diwali drawings for a school competition, a festival chart or a card. The set shows three lit diyas on a rangoli with marigold petals, a house with lamps in every window and a lantern at the door, and two children holding sparklers with fireworks above. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a lantern, sweets or a night sky.',
        useCases: [
            'a Diwali poster for a competition',
            'a festival chart for a class',
            'a card for the festival of lights',
            'a classroom display for Diwali',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Which picture is easiest for a young child?', a: 'The diyas on the rangoli has the fewest parts, with three lamps and simple petal shapes.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
